import { BeneficiaryType, FinancialServiceProviderAdapters } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { AccountView, BeneficiaryView } from '../../../types/entity-types';
import { exhaustiveCheck } from '../../../utils/exhaustive-check';
import { getAmountWithCurrency } from '../../../utils/get-amount-with-currency';
import { PaymentFlowStepWrapper } from '../components/payment-flow-step-wrapper';
import { ReviewStep } from '../components/review-step';
import { EditBeneficiaryFlow } from '../edit-beneficiary-flow/edit-beneficiary-flow';
import { PaymentDetails } from '../payment-flow-storage';
import { usePaymentFlow } from '../use-payment-flow';
import { BeneficiaryDetails, ExternalPaymentForm } from './components/external-payment-form';
import { SuccessStep } from './components/success-step';

export type ExternalPaymentFlowStep = 'PAYMENT_FORM' | 'EDIT_BENEFICIARY' | 'REVIEW' | 'SUCCESS';

type EnhancedBeneficiaryDetails = BeneficiaryDetails & {
  id: BeneficiaryView['id'];
  country: BeneficiaryView['country'];
  emailAddress: BeneficiaryView['emailAddress'];
};

type ExternalPaymentFlowProps = {
  accounts: AccountView[];
  paymentStep: ExternalPaymentFlowStep;
  isProcessingPayment: boolean;
  isEditingBeneficiary: boolean;
  beneficiaryDetails: EnhancedBeneficiaryDetails;
  setPaymentStep: (step: ExternalPaymentFlowStep) => void;
  onPrepareReview: ComponentProps<typeof ExternalPaymentForm>['onSubmit'];
  onSubmit: (paymentDetails: PaymentDetails) => Promise<void>;
  onEditBeneficiary: ComponentProps<typeof ExternalPaymentFlowStep>['onEditBeneficiary'];
  onGoBack: () => void;
  onSuccessfulAuthentication: () => void;
};

export const ExternalPaymentFlow = ({
  accounts,
  paymentStep,
  isProcessingPayment,
  isEditingBeneficiary,
  beneficiaryDetails,
  setPaymentStep,
  onPrepareReview,
  onSubmit,
  onEditBeneficiary,
  onGoBack,
  onSuccessfulAuthentication,
}: ExternalPaymentFlowProps) => {
  const { authorisationStatus, transactionId, clearPaymentDetails } = usePaymentFlow();

  // Cached because it is a dependency of useEffect
  const cachedOnSuccessfulAuthentication = useCallback(() => {
    onSuccessfulAuthentication();
  }, [onSuccessfulAuthentication]);

  const cachedClearPaymentDetails = useCallback(() => {
    clearPaymentDetails();
  }, [clearPaymentDetails]);

  useEffect(() => {
    if (paymentStep === 'PAYMENT_FORM' && authorisationStatus === 'success') {
      cachedOnSuccessfulAuthentication();

      return;
    }

    if (paymentStep === 'PAYMENT_FORM' && authorisationStatus === 'error') {
      toast.error('Authentication failed. Please try again.');
      cachedClearPaymentDetails();

      return;
    }
  }, [authorisationStatus, cachedClearPaymentDetails, cachedOnSuccessfulAuthentication, paymentStep]);

  return (
    <ExternalPaymentFlowStep
      step={paymentStep}
      accounts={accounts}
      beneficiaryDetails={beneficiaryDetails}
      isProcessingPayment={isProcessingPayment}
      isEditingBeneficiary={isEditingBeneficiary}
      transactionId={transactionId}
      onSubmit={onSubmit}
      onEditBeneficiary={onEditBeneficiary}
      onGoBack={onGoBack}
      onPrepareReview={onPrepareReview}
      onSetPaymentStep={setPaymentStep}
    />
  );
};

type ExternalPaymentFlowStepProps = {
  step: ExternalPaymentFlowStep;
  accounts: AccountView[];
  beneficiaryDetails: EnhancedBeneficiaryDetails;
  isProcessingPayment: boolean;
  isEditingBeneficiary: boolean;
  transactionId: string | null;
  onSubmit: (paymentDetails: PaymentDetails) => Promise<void>;
  onGoBack: () => void;
  onPrepareReview: ComponentProps<typeof ExternalPaymentForm>['onSubmit'];
  onSetPaymentStep: (step: ExternalPaymentFlowStep) => void;
  onEditBeneficiary: ComponentProps<typeof EditBeneficiaryFlow>['onSubmit'];
};

const ExternalPaymentFlowStep = ({
  step,
  accounts,
  beneficiaryDetails,
  onSubmit,
  isProcessingPayment,
  isEditingBeneficiary,
  transactionId,
  onGoBack,
  onPrepareReview,
  onSetPaymentStep,
  onEditBeneficiary,
}: ExternalPaymentFlowStepProps) => {
  const { getPaymentDetails, clearPaymentDetails, clearShouldRenderReviewStep } = usePaymentFlow();

  switch (step) {
    case 'PAYMENT_FORM': {
      return (
        <PaymentFlowStepWrapper title={`Make a transfer to ${beneficiaryDetails.name}`} onGoBack={onGoBack}>
          <ExternalPaymentForm
            accounts={accounts}
            isLoading={isProcessingPayment}
            beneficiaryDetails={beneficiaryDetails}
            onCancelTransfer={onGoBack}
            onSubmit={onPrepareReview}
          />
        </PaymentFlowStepWrapper>
      );
    }
    case 'EDIT_BENEFICIARY': {
      // /\s+/ matches one or more whitespace characters (spaces, tabs, newlines, etc.)
      const [firstName, lastName = ''] = beneficiaryDetails.name.trim().split(/\s+/);

      return (
        <EditBeneficiaryFlow
          onGoBack={() => onSetPaymentStep('PAYMENT_FORM')}
          isLoading={isEditingBeneficiary}
          initialValues={{
            currency: beneficiaryDetails.currency,
            country: beneficiaryDetails.country,
            iban:
              beneficiaryDetails.accountDetails.beneficiaryType === BeneficiaryType.IBAN
                ? beneficiaryDetails.accountDetails.iban
                : '',
            accountNumber:
              beneficiaryDetails.accountDetails.beneficiaryType === BeneficiaryType.SCAN
                ? beneficiaryDetails.accountDetails.accountNumber
                : '',
            sortCode:
              beneficiaryDetails.accountDetails.beneficiaryType === BeneficiaryType.SCAN
                ? beneficiaryDetails.accountDetails.sortCode
                : '',
            firstName,
            lastName,
            email: beneficiaryDetails.emailAddress,
          }}
          onSubmit={onEditBeneficiary}
        />
      );
    }
    case 'REVIEW': {
      // Needed to avoid closure when paymentDetails update from null to a value
      const paymentDetails = getPaymentDetails();

      if (
        !paymentDetails?.amount ||
        !paymentDetails?.sourceAccountName ||
        !paymentDetails?.destinationAccountName ||
        !paymentDetails?.currency
      ) {
        Sentry.captureMessage('Payment details are not set in REVIEW step in ExternalPaymentFlow', {
          extra: {
            paymentDetails,
            step,
            message: 'Payment details are not set in REVIEW step in ExternalPaymentFlow',
          },
        });

        return null;
      }

      const { amount, currency, reference, sourceAccountId } = paymentDetails;

      if (!reference) {
        Sentry.captureMessage('Reference is not available when rendering the review step in ExternalPaymentFlow', {
          extra: {
            paymentDetails,
            step,
          },
        });

        return null;
      }

      const sourceAccount = accounts.find((a) => a.id === sourceAccountId);

      if (!sourceAccount) {
        Sentry.captureMessage('Source account is not found in REVIEW step in ExternalPaymentFlow', {
          extra: {
            paymentDetails,
            sourceAccountId,
            accounts: accounts.map((a) => a.id),
            step,
            message: 'Source account is not found in REVIEW step in ExternalPaymentFlow',
          },
        });

        return null;
      }

      return (
        <PaymentFlowStepWrapper
          title="Review Transfer"
          onGoBack={() => {
            onSetPaymentStep('PAYMENT_FORM');
            clearPaymentDetails();
            clearShouldRenderReviewStep();
          }}
        >
          <ReviewStep
            name={beneficiaryDetails.name}
            amount={getAmountWithCurrency({ amount, currency, isAccountBalance: true })}
            currency={beneficiaryDetails.currency}
            accountDetails={beneficiaryDetails.accountDetails}
            latestPayeeCheck={beneficiaryDetails.latestPayeeCheck}
            bankName={sourceAccount.institution?.name}
            bankLogo={sourceAccount.institution?.logo}
            reference={reference}
            isLoading={isProcessingPayment}
            onConfirm={() => onSubmit(paymentDetails)}
            sourceAccountAdapter={sourceAccount.fspAdapter as FinancialServiceProviderAdapters}
          />
        </PaymentFlowStepWrapper>
      );
    }
    case 'SUCCESS': {
      const paymentDetails = getPaymentDetails();

      if (
        !paymentDetails?.amount ||
        !paymentDetails?.sourceAccountName ||
        !paymentDetails?.destinationAccountName ||
        !paymentDetails?.currency ||
        !paymentDetails?.reference
      ) {
        Sentry.captureMessage('Payment details are not set in SUCCESS step in ExternalPaymentFlow', {
          extra: {
            paymentDetails,
            step,
            message: 'Payment details are not set in SUCCESS step in ExternalPaymentFlow',
          },
        });

        return null;
      }

      const { amount, currency, reference } = paymentDetails;

      return (
        <SuccessStep
          recipientName={beneficiaryDetails.name}
          amount={getAmountWithCurrency({ amount, currency, isAccountBalance: true })}
          reference={reference}
          transactionId={transactionId}
          onDone={onGoBack}
        />
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
